import React, { useState } from "react";
import moment from "moment";
import { CircularProgress } from "@mui/material";
import StarRating from "./StarRating";
import { buildAnswers } from "./feedbackUtils";
import { submitFeedback } from "../../api/feedback";

/**
 * One survey: the clinic's questions (the five locked NABH rows first), an
 * optional comment, submit → `onSubmitted(result)` with the backend's
 * `{ message, responseId, analysis }`. API errors are shown inline.
 *
 * `target` = { kind: "request" | "visit" | "public", id, clinicId, clinicName, doctorName, date }
 * `submit` (optional) replaces the authenticated submitter — the logged-out
 * `/survey` page passes the public endpoint; it receives `{ answers, comment? }`.
 */
const SurveyForm = ({ target, survey, onSubmitted, onCancel, submit }) => {
  const [ratings, setRatings] = useState({});
  const [texts, setTexts] = useState({});
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const questions = survey?.questions || [];

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    const { answers, missing } = buildAnswers(questions, ratings, texts);
    if (missing) {
      setError(
        missing.type === "text"
          ? `Please answer: ${missing.text}`
          : `Please rate: ${missing.text}`
      );
      return;
    }
    const payload =
      target.kind === "request"
        ? { requestId: target.id, answers }
        : target.kind === "visit"
          ? { appointmentId: target.id, answers }
          : { answers };
    const trimmed = comment.trim();
    if (trimmed) payload.comment = trimmed;

    setSubmitting(true);
    try {
      const result = await (submit || submitFeedback)(payload);
      onSubmitted(result);
    } catch (err) {
      setError(err?.message || "Could not submit your feedback");
    } finally {
      setSubmitting(false);
    }
  }

  if (!survey || questions.length === 0) {
    return (
      <div className="fb_card">
        <p className="fb_error" role="alert">
          This clinic's survey is not available right now.
        </p>
        <button type="button" className="fb_btn_secondary" onClick={onCancel}>
          Back
        </button>
      </div>
    );
  }

  return (
    <form className="fb_card" onSubmit={onSubmit} noValidate>
      <h2 className="fb_survey_title">{survey.surveyName}</h2>
      <p className="fb_muted">
        {[target.clinicName, target.doctorName].filter(Boolean).join(" · ")}
        {target.date ? ` · ${moment(target.date).format("DD MMM YYYY")}` : ""}
      </p>

      {questions.map((q, i) => (
        <div className="fb_question" key={q.key}>
          <p className="fb_question_text">
            <span className="fb_question_no">{i + 1}.</span> {q.text}
            {q.required !== false && q.type !== "text" ? (
              <span className="fb_required" aria-hidden="true">
                {" "}
                *
              </span>
            ) : null}
          </p>
          {q.type === "text" ? (
            <textarea
              className="fb_textarea"
              rows={3}
              aria-label={q.text}
              value={texts[q.key] || ""}
              onChange={(e) =>
                setTexts((prev) => ({ ...prev, [q.key]: e.target.value }))
              }
            />
          ) : (
            <StarRating
              name={q.text}
              value={ratings[q.key] ?? null}
              onChange={(n) =>
                setRatings((prev) => ({ ...prev, [q.key]: n }))
              }
            />
          )}
        </div>
      ))}

      <div className="fb_question">
        <label className="fb_question_text" htmlFor="fb_comment">
          Anything else you would like to tell us? (optional)
        </label>
        <textarea
          id="fb_comment"
          className="fb_textarea"
          rows={3}
          maxLength={2000}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </div>

      {error ? (
        <p className="fb_error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="fb_actions">
        <button
          type="button"
          className="fb_btn_secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button type="submit" className="fb_btn_primary" disabled={submitting}>
          {submitting ? (
            <CircularProgress sx={{ color: "white" }} size="1rem" />
          ) : (
            "Submit feedback"
          )}
        </button>
      </div>
    </form>
  );
};

export default SurveyForm;
